import React, { useEffect, useState } from "react";
import PriceMarkup from "../../../component/views/seller/settings/priceMarkup";
import InventorySync from "../../../component/views/seller/settings/inventorySync";
import PriceSync from "../../../component/views/seller/settings/priceSync";
import WebInput from "../../../component/library/webInput";
import WebButton from "../../../component/library/webButton";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { useLoader } from "../../../base/context/LoaderProvider";
import { WalletApiService } from "../../../service/seller/walletApiService";
import { SettingApiService } from "../../../service/seller/settingApiService";
import "./style.css";

export default function Settings() {
  const message = useMessage();
  const loader = useLoader();

  const [wallet, setWallet] = useState({});
  const [autoPay, setAutoPay] = useState();
  const [autoRenewed, setAutoRenewed] = useState();
  const [stateUpdate, setStateUpdate] = useState(false);

  const [data, setData] = useState({
    amazonSpPercent: "",
    shopifySpPercent: "",
    amazonMrpPercent: "",
    shopifyMrpPercent: "",
    inventorySync: {
      amazon: false,
      shopify: false,
    },
    priceSync: {
      amazon: false,
      shopify: false,
    },
    brandName: "",
    companyName: "",
  });

  const [error, setError] = useState({
    amazonSpPercent: "",
    shopifySpPercent: "",
    amazonMrpPercent: "",
    shopifyMrpPercent: "",
    inventorySync: {
      amazon: false,
      shopify: false,
    },
    priceSync: {
      amazon: false,
      shopify: false,
    },
    brandName: "",
    companyName: "",
  });

  const handleOnChange = (e) => {
    if (e.target.name === "brandImage") {
      setData({ ...data, [e.target.name]: e.target.files[0] });
      setError({ ...error, [e.target.name]: "" });
    } else {
      setData({ ...data, [e.target.name]: e.target.value });
      setError({ ...error, [e.target.name]: "" });
    }
  };

  const handleOnClick = (key, name, value) => {
    setData({
      ...data,
      [key]: { ...data[key], [name]: value },
    });
    setStateUpdate(true);
  };

  const handleSettings = async () => {
    await SettingApiService.getSettings()
      .then(async (res) => {
        if (res.status.code === 200) {
          setData(res.data);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(
          err?.message || "Server error. Try again later",
          ToastTypes.ERROR
        );
      });
  };

  const handleWallet = async () => {
    await WalletApiService.getWalet()
      .then(async (res) => {
        if (res.status.code === 200) {
          setWallet(res.data);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(
          err?.message || "Server error. Try again later",
          ToastTypes.ERROR
        );
      });
  };

  const handleAutoRewel = async () => {
    loader.showLoading(true);
    await WalletApiService.getAutoRenewel()
      .then(async (res) => {
        loader.showLoading(false);

        if (res.status.code === 200) {
          setAutoRenewed(!autoRenewed);
          message.showToast(res.status.message, ToastTypes.SUCCESS);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(
          err?.message || "Server error. Try again later",
          ToastTypes.ERROR
        );
      });
  };

  const handleAutoPay = async () => {
    loader.showLoading(true);
    await WalletApiService.getAutoPay()
      .then(async (res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast(res.status.message, ToastTypes.SUCCESS);
          setAutoPay(!autoPay);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(
          err?.message || "Server error. Try again later",
          ToastTypes.ERROR
        );
      });
  };

  const handleSave = () => {
    loader.showLoading(true);
    SettingApiService.updateSettings(JSON.stringify(data))
      .then(async (res) => {
        setStateUpdate(false);
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast(res.status.message, ToastTypes.SUCCESS);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        setStateUpdate(false);
        loader.showLoading(false);
        message.showToast(
          err?.message || "Server error. Try again later",
          ToastTypes.ERROR
        );
      });
  };

  useEffect(() => {
    handleWallet();
    handleSettings();
  }, []);

  useEffect(() => {
    setAutoPay(wallet?.wallet?.autoPay);
    setAutoRenewed(wallet?.wallet?.autoRenewed);
  }, [wallet]);

  useEffect(() => {
    if (stateUpdate) {
      handleSave();
    }
  }, [data]);

  return (
    <div className="setting_bg">
      <div className="settings_header">
        <h1 className="settings_title">Marketplace Store Settings</h1>
        <p className="settings_subtitle">
          Manage your account preferences and configurations
        </p>
      </div>

      <div className="settings_container">
        <div className="settings_card">
          <PriceMarkup
            data={data}
            error={error}
            handleOnChange={handleOnChange}
            onSubmit={handleSave}
            refresh={handleSettings}
          />
        </div>

        <div className="settings_grid">
          <div className="settings_card">
            <InventorySync
              data={data}
              handleOnClick={handleOnClick}
              onSubmit={handleSave}
            />
          </div>

          <div className="settings_card">
            <PriceSync
              data={data}
              handleOnClick={handleOnClick}
              onSubmit={handleSave}
            />
          </div>
        </div>

        <div className="settings_grid">
          <div className="settings_card">
            <div className="settings_card_header">
              <h3 className="settings_card_title">Order Amount</h3>
              <p className="settings_card_description">
                Automatically pay for orders
              </p>
            </div>
            <div className="toggle_container">
              <label className="modern_toggle">
                <input
                  type="checkbox"
                  checked={autoPay}
                  onChange={(e) => {
                    e.preventDefault();
                    handleAutoPay();
                  }}
                />
                <span className="toggle_slider"></span>
              </label>
              <span className="toggle_label">Enable Auto-Pay</span>
            </div>
          </div>

          <div className="settings_card">
            <div className="settings_card_header">
              <h3 className="settings_card_title">Member Fee</h3>
              <p className="settings_card_description">
                Automatically renew membership
              </p>
            </div>
            <div className="toggle_container">
              <label className="modern_toggle">
                <input
                  type="checkbox"
                  checked={autoRenewed}
                  onChange={(e) => {
                    e.preventDefault();
                    handleAutoRewel();
                  }}
                />
                <span className="toggle_slider"></span>
              </label>
              <span className="toggle_label">Enable Auto-Renewal</span>
            </div>
          </div>
        </div>

        <div className="settings_card">
          <div className="settings_card_header">
            <h3 className="settings_card_title">Brand Information</h3>
            <p className="settings_card_description">
              Update your brand and company details
            </p>
          </div>

          <div className="settings_form">
            {/* Brand + Company in same row for big screens */}
            <div className="settings_form_row">
              <div className="form_group">
                <label className="form_label">Brand Name</label>
                <WebInput
                  id="brandName"
                  name="brandName"
                  error={error.brandName}
                  value={data.brandName}
                  onChange={handleOnChange}
                  className="settings_input"
                />
              </div>

              <div className="form_group">
                <label className="form_label">Company Name</label>
                <WebInput
                  id="companyName"
                  name="companyName"
                  error={error.companyName}
                  value={data.companyName}
                  onChange={handleOnChange}
                  className="settings_input"
                />
              </div>
            </div>

            <div className="form_actions">
              <WebButton
                className="setting_submit_btn"
                text="Save Changes"
                onClick={handleSave}
                type="submit"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
